import { yupResolver } from '@hookform/resolvers/yup';
import { useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  DayLimitArrangementFormValues,
  DayLimitArrangementResponse
} from '../../types';

import { useQueryClient } from 'react-query';
import dayjs from 'dayjs';

import { LoadingButton } from '@mui/lab';
import { Divider, IconButton } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';

import { FormComponentProps } from 'components/shared/CRUD/TableCRUDLayout';
import MultiStepForm from 'components/shared/MultiStepForm/MultiStepForm';
import CLASelect from 'components/shared/CLASelect/CLASelect';
import { ControlledDatePicker, ControlledTextField } from 'components/shared';
import useUpdateDayLimitArrangement from '../../hooks/useUpdateDayLimitArrangement';
import useCreateDayLimitArrangement from '../../hooks/useCreateDayLimitArrangement';
import dayLimitArrangementSchema from './daylimitarrangement-schema';
import {
  StyledDayLimitBaseBox,
  StyledDayLimitErrorAlert,
  StyledNoRowsBox,
  StyledRecordsBox,
  StyledRecordsInnerBox
} from './CreateEditDayLimitArrangementsForm.styles';
import {
  GridCompact12,
  GridCompact4,
  GridContainer
} from 'components/shared/Grid/Grid.components';
import React from 'react';
import { StyledTypographyBodyBold } from 'components/shared/Typography/Typography.styles';
import {
  StyledBoxFlexAlignCenter,
  StyledBoxFlexSmallGap
} from 'components/shared/Box/Box.styles';
import DrawerFooter from 'components/shared/Drawer/DrawerFooter';
import { Icon } from '@sdb/planning-ui';

// Mapping ------------------------------------------------------------
const toFormValues = (
  response: DayLimitArrangementResponse
): DayLimitArrangementFormValues => ({
  id: response.id,
  claId: response.cla?.claId,
  validFrom: response.validFrom ? new Date(response.validFrom) : null,
  validTo: response.validTo ? new Date(response.validTo) : null,
  records: (response.records || []).map((r) => ({
    id: r.id,
    fromHours: r.fromHours,
    toHoursInclusive: r.toHoursInclusive,
    maxNumberOfDays: r.maxNumberOfDays
  }))
});

const toRequestPayload = (values: DayLimitArrangementFormValues) => {
  const base: any = {
    claId: values.claId == null ? null : values.claId,
    validFrom: values.validFrom
      ? dayjs(values.validFrom).format('YYYY-MM-DD')
      : null,
    validTo: values.validTo ? dayjs(values.validTo).format('YYYY-MM-DD') : null,
    records: values.records.map((r) => ({
      fromHours: r.fromHours,
      toHoursInclusive: r.toHoursInclusive,
      maxNumberOfDays: r.maxNumberOfDays
    }))
  };
  return values.id ? { id: values.id, ...base } : base;
};

// Component ----------------------------------------------------------
export default function CreateEditDayLimitArrangementsForm({
  handleClose,
  rowData
}: FormComponentProps<DayLimitArrangementResponse>) {
  const { t } = useTranslation('common');
  const formData = rowData ? toFormValues(rowData) : null;

  useQueryClient(); // retained if future invalidations needed locally
  const updateMutation = useUpdateDayLimitArrangement();
  const createMutation = useCreateDayLimitArrangement();
  // const { mutateAsync: createMutation, isLoading: isCreating } = useMutation(
  //   (payload: any) => axios.post(DAY_LIMIT_ARRANGEMENTS_CREATE, payload),
  //   {
  //     onSuccess: async () => {
  //       await queryClient.invalidateQueries(['dayLimitArrangements', 'list']);
  //       showSnackbar({ severity: 'success', message: i18n.t('changesSaved') });
  //     },
  //     onError: (err: any) => showError(err)
  //   }
  // );

  // const { mutateAsync: updateMutation, isLoading: isUpdating } = useMutation(
  //   (payload: any) => axios.put(DAY_LIMIT_ARRANGEMENTS_UPDATE, payload),
  //   {
  //     onSuccess: async () => {
  //       await queryClient.invalidateQueries(['dayLimitArrangements', 'list']);
  //       showSnackbar({ severity: 'success', message: i18n.t('changesSaved') });
  //     },
  //     onError: (err: any) => showError(err)
  //   }
  // );

  const methods = useForm<DayLimitArrangementFormValues>({
    defaultValues: formData ?? {
      claId: undefined,
      validFrom: null,
      validTo: null,
      records: []
    },
    resolver: yupResolver(dayLimitArrangementSchema(t))
  });

  const {
    handleSubmit,
    control,
    formState: { errors }
  } = methods;
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'records'
  });

  const addRecord = () =>
    append({ fromHours: 0, toHoursInclusive: 0, maxNumberOfDays: 1 });

  const onSubmit = (data: DayLimitArrangementFormValues) => {
    const payload = toRequestPayload(data);
    if (data.id) {
      updateMutation.mutate(payload, { onSuccess: handleClose });
    } else {
      createMutation.mutate(payload, { onSuccess: handleClose });
    }
  };

  return (
    <MultiStepForm onSubmit={handleSubmit(onSubmit)} methods={methods}>
      <MultiStepForm.Section
        title={t('dayLimitArrangements.formSectionLabels.base')}
        validateFields={['claId', 'validFrom', 'validTo']}
      >
        <StyledDayLimitBaseBox>
          <CLASelect />
          <ControlledDatePicker
            control={control}
            label={t('dayLimitArrangements.form.validFrom')}
            name="validFrom"
          />
          <ControlledDatePicker
            control={control}
            label={t('dayLimitArrangements.form.validTo')}
            name="validTo"
          />
        </StyledDayLimitBaseBox>
      </MultiStepForm.Section>

      <MultiStepForm.Section
        title={t('dayLimitArrangements.formSectionLabels.records')}
        validateFields={['records']}
      >
        <StyledRecordsBox>
          {errors.records?.message && (
            <StyledDayLimitErrorAlert
              severity="error"
              variant="standard"
              data-testid="records-error"
            >
              {errors.records.message}
            </StyledDayLimitErrorAlert>
          )}

          <StyledRecordsInnerBox>
            <IconButton
              color="primary"
              onClick={addRecord}
              size="small"
              aria-label={t('add')}
            >
              <AddIcon />
            </IconButton>
          </StyledRecordsInnerBox>

          <GridContainer>
            <GridCompact4>
              <StyledTypographyBodyBold>
                {t('dayLimitArrangements.form.record.fromHours')}
              </StyledTypographyBodyBold>
            </GridCompact4>
            <GridCompact4>
              <StyledTypographyBodyBold>
                {t('dayLimitArrangements.form.record.toHoursInclusive')}
              </StyledTypographyBodyBold>
            </GridCompact4>
            <GridCompact4>
              <StyledTypographyBodyBold>
                {t('dayLimitArrangements.form.record.maxNumberOfDays')}
              </StyledTypographyBodyBold>
            </GridCompact4>

            <GridCompact12>
              <Divider />
            </GridCompact12>

            {fields.map((f, idx) => (
              <React.Fragment key={f.id}>
                <GridCompact4>
                  <ControlledTextField
                    type="number"
                    control={control}
                    name={`records.${idx}.fromHours`}
                    variant="outlined"
                    size="small"
                  />
                </GridCompact4>
                <GridCompact4>
                  <ControlledTextField
                    type="number"
                    control={control}
                    name={`records.${idx}.toHoursInclusive`}
                    variant="outlined"
                    size="small"
                  />
                </GridCompact4>
                <GridCompact4>
                  <StyledBoxFlexSmallGap>
                    <ControlledTextField
                      type="number"
                      control={control}
                      name={`records.${idx}.maxNumberOfDays`}
                      variant="outlined"
                      size="small"
                    />
                    <StyledBoxFlexAlignCenter>
                      <IconButton
                        size="small"
                        aria-label={t('delete')}
                        onClick={() => remove(idx)}
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </StyledBoxFlexAlignCenter>
                  </StyledBoxFlexSmallGap>
                </GridCompact4>
              </React.Fragment>
            ))}
          </GridContainer>
          {fields.length === 0 && !errors.records?.message && (
            <StyledNoRowsBox>
              <Icon type="box-open" color="primary" />
              {t('dayLimitArrangements.form.noRecords')}
            </StyledNoRowsBox>
          )}
        </StyledRecordsBox>
      </MultiStepForm.Section>

      <DrawerFooter>
        <LoadingButton
          loading={createMutation.isLoading || updateMutation.isLoading}
          type="submit"
          color="primary"
          variant="contained"
        >
          {t('save')}
        </LoadingButton>
      </DrawerFooter>
    </MultiStepForm>
  );
}
